import { useLang } from 'hooks/lang';

import { FullProject, loaders } from 'entities';

import { Column, Row, ResponsiveOverlay, Skeleton, Card, Dot } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';

import { ArrowUpIcon } from 'components/Icons';

import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { dateDistanceToNow } from 'utils/date';
import { getFirstAndLastName } from 'utils/text';

interface ChangeHistoryModalProps {
  children?: never;
  property: string | null;
  close: () => void;
}

const propertyToTranslationKey: { [key: string]: string } = {
  name: 'project-name',
};

export function ChangeHistoryModal({ property, close }: ChangeHistoryModalProps) {
  const { t } = useLang();
  const { project } = useProjectProfile();

  const { loading, error, data } = loaders.projects.projectPropertyHistory.use({
    defaultValue: [],
    blockLoad: !property,

    params: {
      property: property!,
      project: project.id,
    },
  });

  const isLoading = loading || error;

  return (
    <ResponsiveOverlay
      default="sheet"
      isOpen={!!property}
      close={close}
      title={t(propertyToTranslationKey[`${property}`]) as string}
      className="overflow-y-auto"
    >
      {isLoading ? (
        <Skeleton height={55} quantity={3} />
      ) : (
        <Column gap="6" relative>
          <div className="absolute top-1/2 h-[calc(100%-2rem-3px)] w-0.5 -translate-y-1/2 rounded-full bg-primary" />

          <div className="relative pl-3">
            <Dot color="primary" className="absolute -left-1 top-1/2 -translate-y-1/2" size={10} />

            <div className="rounded-md border border-transparent p-2 font-medium text-primary">{`${project[property as keyof FullProject]}`}</div>
          </div>

          {data
            .slice()
            .reverse()
            .map(({ changedBy, id, justification, oldValue, timestamp }) => (
              <Column key={id} className="pl-3">
                <Card flow="column" gap="2" spacing="small">
                  <Row gap="2">
                    <ArrowUpIcon color="primary" />

                    <UserAvatar size={24} className="text-xs" user={changedBy} />

                    <div>
                      <p>{getFirstAndLastName(changedBy.name)}</p>
                      <p className="text-xxs opacity-70">{dateDistanceToNow(timestamp)}</p>
                    </div>
                  </Row>

                  <p className="pl-1 text-sm italic">{justification}</p>
                </Card>

                <div className="relative p-2">
                  <Dot
                    color="primary"
                    className="absolute -left-4 top-1/2 -translate-y-1/2"
                    size={10}
                  />

                  {oldValue}

                  <span className="opacity-50"></span>
                </div>
              </Column>
            ))}
        </Column>
      )}
    </ResponsiveOverlay>
  );
}
